import React from "react";
import "./Loading.css";

// Componente principal de Loading que puede mostrar diferentes tipos de loaders
const Loading = ({
  type = "modern",
  containerClass = "loading-container",
  width = "100%",
  height = "100%",
  borderRadius = "8px",
  withPulse = true,
  withWave = true,
  color = "#e6e6e6",
  content = null,
  message = "Cargando información...",
}) => {
  switch (type) {
    case "skeleton":
      return <DashboardSkeleton />;
    case "profile":
      return <ProfileSkeleton />;
    case "report":
      return <ReportSkeleton />;
    case "dashboard-reports":
      return <DashboardReportsSkeleton />;
    case "modern":
      return <ModernLoader message={message} />;
    case "dots":
      return <DotsLoader message={message} />;
    case "progress":
      return <ProgressLoader message={message} />;
    case "pulse":
      return <PulseLoader message={message} />;
    default:
      return (
        <div
          className={containerClass}
          style={{
            width: width,
            height: height,
          }}
        >
          <div
            className={`adaptive-skeleton ${withPulse ? "skeleton-pulse" : ""}`}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: borderRadius,
              backgroundColor: color,
            }}
          >
            {content}
            {withWave && <div className="skeleton-wave-overlay"></div>}
          </div>
        </div>
      );
  }
};

// Loader moderno con logo animado y barras de progreso
const ModernLoader = ({ message }) => {
  return (
    <div className="modern-loader-container">
      <div className="modern-loader-content">
        {/* Logo DARPHA animado */}
        <div className="loader-logo-container">
          <svg
            className="loader-logo"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient
                id="logoGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="var(--accent-color)" />
                <stop offset="100%" stopColor="var(--text-primary)" />
              </linearGradient>
            </defs>
            <polygon
              points="50,10 90,35 90,65 50,90 10,65 10,35"
              fill="none"
              stroke="url(#logoGradient)"
              strokeWidth="2"
              className="logo-hexagon"
            />
            <circle
              cx="50"
              cy="50"
              r="15"
              fill="var(--accent-color)"
              className="logo-center"
            />
          </svg>
          <div className="loader-ripple">
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        {/* Barras de progreso animadas */}
        <div className="loader-bars">
          <div className="loader-bar bar-1"></div>
          <div className="loader-bar bar-2"></div>
          <div className="loader-bar bar-3"></div>
        </div>

        <p className="loader-message">{message}</p>

        {/* Indicador de puntos */}
        <div className="loader-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
};

// Loader con puntos flotantes
const DotsLoader = ({ message }) => {
  return (
    <div className="dots-loader-container">
      <div className="dots-loader-content">
        <div className="floating-dots">
          <div className="dot-item dot-1"></div>
          <div className="dot-item dot-2"></div>
          <div className="dot-item dot-3"></div>
          <div className="dot-item dot-4"></div>
          <div className="dot-item dot-5"></div>
          <div className="dot-item dot-6"></div>
        </div>
        <p className="loader-message">{message}</p>
      </div>
    </div>
  );
};

// Loader con barra de progreso lineal
const ProgressLoader = ({ message }) => {
  return (
    <div className="progress-loader-container">
      <div className="progress-loader-content">
        <div className="progress-icon">
          <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="var(--accent-color)"
              strokeWidth="2"
              className="progress-circle"
            />
            <path
              d="M 25 15 L 25 25 L 32 32"
              fill="none"
              stroke="var(--accent-color)"
              strokeWidth="2"
              strokeLinecap="round"
              className="progress-hand"
            />
          </svg>
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>

        <p className="loader-message">{message}</p>
      </div>
    </div>
  );
};

// Loader con pulso concéntrico
const PulseLoader = ({ message }) => {
  return (
    <div className="pulse-loader-container">
      <div className="pulse-loader-content">
        <div className="pulse-rings">
          <div className="pulse-ring ring-1"></div>
          <div className="pulse-ring ring-2"></div>
          <div className="pulse-ring ring-3"></div>
          <div className="pulse-core"></div>
        </div>
        <p className="loader-message">{message}</p>
      </div>
    </div>
  );
};

// Componentes skeleton existentes sin cambios
const DashboardSkeleton = () => {
  return (
    <div className="dashboard-skeleton">
      <div className="dashboard-skeleton-layout">
        <div className="skeleton-column-small">
          <div className="skeleton-card skeleton-pulse"></div>
          <div className="skeleton-card skeleton-pulse"></div>
          <div className="skeleton-card skeleton-pulse"></div>
          <div className="skeleton-card skeleton-pulse"></div>
        </div>
        <div className="skeleton-column-big">
          <div className="skeleton-card skeleton-chart skeleton-pulse">
            <div className="skeleton-chart-title"></div>
            <div className="skeleton-chart-content">
              <div className="skeleton-chart-line"></div>
              <div className="skeleton-chart-line"></div>
              <div className="skeleton-chart-line"></div>
            </div>
          </div>
          <div className="skeleton-card skeleton-pulse">
            <div className="skeleton-collaborators-title"></div>
            <div className="skeleton-collaborators-item"></div>
            <div className="skeleton-collaborators-item"></div>
            <div className="skeleton-collaborators-item"></div>
          </div>
        </div>
      </div>
      <div className="skeleton-wave-overlay"></div>
    </div>
  );
};

const ProfileSkeleton = () => {
  // Contenido existente
  return (
    <div className="profile-skeleton">
      <div className="skeleton-profile-image skeleton-pulse"></div>

      <div className="skeleton-profile-section">
        <div className="skeleton-profile-label"></div>
        <div className="skeleton-profile-value"></div>
      </div>

      <div className="skeleton-profile-section">
        <div className="skeleton-profile-label"></div>
        <div className="skeleton-profile-value"></div>
      </div>

      <div className="skeleton-profile-buttons">
        <div className="skeleton-profile-button"></div>
        <div className="skeleton-profile-button"></div>
      </div>

      <div className="skeleton-profile-documents-header"></div>
      <div className="skeleton-profile-documents-buttons">
        <div className="skeleton-profile-document-button"></div>
        <div className="skeleton-profile-document-button"></div>
        <div className="skeleton-profile-document-button"></div>
      </div>

      <div className="skeleton-profile-logout"></div>

      <div className="skeleton-wave-overlay"></div>
    </div>
  );
};

const ReportSkeleton = () => {
  // Contenido existente
  return (
    <div className="report-skeleton">
      <div className="skeleton-report-header">
        <div className="skeleton-report-buttons">
          <div className="skeleton-report-button skeleton-pulse"></div>
        </div>
      </div>

      <div className="skeleton-report-sections">
        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-data-row">
            <div className="skeleton-report-data-label"></div>
            <div className="skeleton-report-data-value"></div>
          </div>
          <div className="skeleton-report-data-row">
            <div className="skeleton-report-data-label"></div>
            <div className="skeleton-report-data-value"></div>
          </div>
          <div className="skeleton-report-data-row">
            <div className="skeleton-report-data-label"></div>
            <div className="skeleton-report-data-value"></div>
          </div>
        </div>

        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-paragraph"></div>
          <div className="skeleton-report-paragraph"></div>
        </div>

        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-paragraph"></div>
        </div>

        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-checklist">
            <div className="skeleton-report-checklist-item"></div>
            <div className="skeleton-report-checklist-item"></div>
            <div className="skeleton-report-checklist-item"></div>
            <div className="skeleton-report-checklist-item"></div>
            <div className="skeleton-report-checklist-item"></div>
          </div>
        </div>

        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-images">
            <div className="skeleton-report-image"></div>
            <div className="skeleton-report-image"></div>
            <div className="skeleton-report-image"></div>
          </div>
        </div>

        <div className="skeleton-report-section skeleton-pulse">
          <div className="skeleton-report-section-title"></div>
          <div className="skeleton-report-signatures">
            <div className="skeleton-report-signature"></div>
            <div className="skeleton-report-signature"></div>
          </div>
        </div>
      </div>

      <div className="skeleton-wave-overlay"></div>
    </div>
  );
};

const DashboardReportsSkeleton = () => {
  // Función para crear filas skeleton para las tablas
  const renderTableRows = (count) => {
    return Array(count)
      .fill()
      .map((_, index) => (
        <div key={index} className="skeleton-table-row skeleton-pulse">
          <div className="skeleton-table-cell-small"></div>
          <div className="skeleton-table-cell"></div>
          <div className="skeleton-table-cell"></div>
          <div className="skeleton-table-cell"></div>
          <div className="skeleton-table-cell-small"></div>
        </div>
      ));
  };

  return (
    <div className="dashboard-reports-skeleton">
      <div className="dashboard-reports-layout">
        <div className="skeleton-report-table-section">
          <div className="skeleton-report-table-header skeleton-pulse">
            <div className="skeleton-report-title"></div>
            <div className="skeleton-report-filters">
              <div className="skeleton-filter-button"></div>
              <div className="skeleton-filter-button"></div>
            </div>
          </div>
          <div className="skeleton-table">
            <div className="skeleton-table-header skeleton-pulse">
              <div className="skeleton-table-header-cell-small"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell-small"></div>
            </div>
            <div className="skeleton-table-body">{renderTableRows(5)}</div>
          </div>
        </div>

        <div className="skeleton-report-table-section">
          <div className="skeleton-report-table-header skeleton-pulse">
            <div className="skeleton-report-title"></div>
            <div className="skeleton-report-filters">
              <div className="skeleton-filter-button"></div>
              <div className="skeleton-filter-button"></div>
            </div>
          </div>
          <div className="skeleton-table">
            <div className="skeleton-table-header skeleton-pulse">
              <div className="skeleton-table-header-cell-small"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell"></div>
              <div className="skeleton-table-header-cell-small"></div>
            </div>
            <div className="skeleton-table-body">{renderTableRows(5)}</div>
          </div>
        </div>
      </div>
      <div className="skeleton-wave-overlay"></div>
    </div>
  );
};

export default Loading;
